import { useLocation } from "@tanstack/react-router";
import {
	createContext,
	type ReactNode,
	type RefObject,
	useContext,
	useEffect,
	useRef,
} from "react";
import { useAppliedViews } from "@/components/views/use-applied-views";
import { workspaceViews } from "@/components/views/views-data";
import { useCurrentOrg } from "@/hooks/use-current-org";
import { useOrgContext } from "@/hooks/use-org-context";
import {
	isSettingsDestination,
	settingsDestinations,
} from "@/lib/workspace-navigation";

interface WorkspaceReturn {
	owner: string;
	pathname: string;
	search: Record<string, unknown>;
	hash: string;
}

const WorkspaceReturnContext =
	createContext<RefObject<WorkspaceReturn | null> | null>(null);

/** Keep the return destination when the shell swaps sidebar and compact nav. */
export function WorkspaceNavigationProvider({
	children,
}: {
	children: ReactNode;
}) {
	const previous = useRef<WorkspaceReturn | null>(null);
	return (
		<WorkspaceReturnContext.Provider value={previous}>
			{children}
		</WorkspaceReturnContext.Provider>
	);
}

/** Shared by the wide sidebar and narrow navigation. */
export function useWorkspaceNavigation() {
	const { currentOwner, orgContext } = useOrgContext();
	const location = useLocation();
	const owner =
		currentOwner && !currentOwner.startsWith("@") ? currentOwner : null;
	const {
		isMember,
		isPublic,
		isLoading: isMembershipLoading,
	} = useCurrentOrg(owner ?? undefined);
	const settingsPath =
		!!owner && isSettingsDestination(owner, location.pathname);
	const settings = isMember && settingsPath;
	const { data, isError, error, refetch } = useAppliedViews({
		...orgContext,
		organizationId: isMember ? orgContext.organizationId : null,
	});
	// A public workspace publishes its view list, so a visitor gets the same
	// tabs a member does. Membership still decides everything else here.
	const canBrowseViews = isMember || isPublic;
	const views = canBrowseViews ? workspaceViews(data ?? []) : [];
	const localPrevious = useRef<WorkspaceReturn | null>(null);
	const sharedPrevious = useContext(WorkspaceReturnContext);
	const previous = sharedPrevious ?? localPrevious;
	useEffect(() => {
		// Membership can still be loading on a settings deep link. Never remember
		// that route as the destination for "Back to workspace".
		if (owner && !settingsPath)
			previous.current = {
				owner,
				pathname: location.pathname,
				search: location.search,
				hash: location.hash,
			};
	}, [
		owner,
		settingsPath,
		location.pathname,
		location.search,
		location.hash,
		previous,
	]);
	const returnTo =
		previous.current?.owner === owner
			? previous.current
			: { pathname: owner ? `/${owner}` : "/", search: {}, hash: "" };
	return {
		owner,
		isMember,
		isMembershipLoading,
		settings,
		views,
		returnTo,
		location,
		viewsError: canBrowseViews && isError ? error : null,
		retryViews: () => void refetch(),
		destinations: owner
			? settingsDestinations(
					owner,
					location.pathname,
					location.search ?? {},
					location.hash,
				)
			: [],
	};
}
